import React from 'react';
import { Download, Loader2 } from 'lucide-react';
import type { GenerateReportResult, ReportType } from '../../types/report.types';
import { FinancialSummaryReportSections } from './FinancialSummaryReportSections';

interface SummaryProps {
  title: string;
  dateRangeLabel: string;
  format: string;
  categoryCount: number;
  accountCount: number;
  businessClaimsUpdated?: number;
  businessClaimsSkipped?: number;
}

interface Props {
  phase: 'summary' | 'generating' | 'success' | 'error';
  summary: SummaryProps;
  reportType?: ReportType;
  result?: GenerateReportResult | null;
  errorMessage?: string | null;
  onDownload: () => void;
  onRetry: () => void;
}

export function ReportWizardStepGenerate({
  phase,
  summary,
  reportType,
  result,
  errorMessage,
  onDownload,
  onRetry,
}: Props): React.ReactElement {
  if (phase === 'generating') {
    return (
      <div className="py-8 text-center" role="status" aria-live="polite">
        <Loader2 className="mx-auto h-10 w-10 animate-spin text-blue-600" aria-hidden />
        <p className="mt-4 font-semibold text-gray-900">Generating Report…</p>
        <p className="mt-1 text-sm text-gray-600">Please wait while we process your data</p>
        <div className="mx-auto mt-6 h-2 w-full max-w-xs overflow-hidden rounded-full bg-gray-200">
          <div className="h-full w-2/3 animate-pulse rounded-full bg-blue-600" />
        </div>
      </div>
    );
  }

  if (phase === 'success' && result?.status === 'succeeded') {
    return (
      <div className="py-8 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600">
          ✓
        </div>
        <p className="mt-4 font-semibold text-gray-900">Report Generated Successfully!</p>
        <p className="mt-1 text-sm text-gray-600">Your report is ready to download</p>
        <button
          type="button"
          onClick={onDownload}
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Download className="h-4 w-4" aria-hidden />
          Download Report
        </button>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="py-8 text-center">
        <p className="font-semibold text-red-600">Report generation failed</p>
        <p className="mt-2 text-sm text-gray-600">{errorMessage ?? 'Please try again.'}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-blue-50 p-4">
      <h3 className="font-semibold text-gray-900">Report Summary</h3>
      <dl className="mt-3 space-y-2 text-sm text-gray-700">
        <div className="flex justify-between gap-4">
          <dt>Title</dt>
          <dd className="font-medium">{summary.title}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt>Date Range</dt>
          <dd className="font-medium">{summary.dateRangeLabel}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt>Format</dt>
          <dd className="font-medium">{summary.format}</dd>
        </div>
        {reportType === 'business_expense' ? (
          <div className="flex justify-between gap-4">
            <dt>Business-use updates</dt>
            <dd className="font-medium text-right">
              {summary.businessClaimsUpdated != null && summary.businessClaimsUpdated > 0
                ? `${summary.businessClaimsUpdated} transaction${
                    summary.businessClaimsUpdated === 1 ? '' : 's'
                  } updated`
                : 'None (skipped or already set)'}
              {summary.businessClaimsSkipped != null && summary.businessClaimsSkipped > 0
                ? ` · ${summary.businessClaimsSkipped} kept existing`
                : ''}
            </dd>
          </div>
        ) : (
          <>
            <div className="flex justify-between gap-4">
              <dt>Categories</dt>
              <dd className="font-medium">
                {summary.categoryCount === 0 ? 'All categories' : `${summary.categoryCount} categories`}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt>Accounts</dt>
              <dd className="font-medium">
                {summary.accountCount === 0 ? 'All accounts' : `${summary.accountCount} accounts`}
              </dd>
            </div>
          </>
        )}
      </dl>
      {reportType === 'monthly_financial_summary' && <FinancialSummaryReportSections />}
    </div>
  );
}
